import React, { memo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import { LinkItem } from '@atlaskit/side-navigation';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { BulkIcon } from '@confluence/manage-pages/entry-points/BulkIcon';
import { SPACE_BULK_TOOLS } from '@confluence/named-routes';
import { usePageSpaceKey } from '@confluence/page-context';
import { PremiumLozengeLoadable } from '@confluence/trial-components';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { useRouteName } from '@confluence/route-manager';

import { cssFn } from './LegacySpaceLinks';

const i18n = defineMessages({
	bulkToolsTooltip: {
		id: 'side-navigation.space-navigation.bulk-tools.tooltip',
		defaultMessage: 'Manage content for this space',
		description: 'Tooltip message on hover of bulk tools space link',
	},
	bulkToolsItemLabel: {
		id: 'side-navigation.space-navigation.bulk-tools.label',
		defaultMessage: 'Content manager',
		description: 'Content Manager (bulk content tools) Menu Item label',
	},
});

type BulkToolsNavigationProps = {
	testId: string;
	onClick?: () => void;
};

export const BulkToolsNavigation = memo(({ testId, onClick }: BulkToolsNavigationProps) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const [spaceKey] = usePageSpaceKey();
	const isSelected = useRouteName(isOnBulkToolsArgs);
	const iconStrokeColor = isSelected
		? token('color.icon.selected', '#0C66E4')
		: token('color.icon', '#42526E');

	return (
		<CustomItem data-vc="bulk-tools-navigation" {...ssrPlaceholderIdProp}>
			<Tooltip position="top" content={<FormattedMessage {...i18n.bulkToolsTooltip} />}>
				{(tooltipProps) => (
					<LinkItem
						// eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis
						cssFn={cssFn}
						key="bulk-tools"
						iconBefore={<BulkIcon strokeColor={iconStrokeColor} />}
						isSelected={isSelected}
						href={SPACE_BULK_TOOLS.toUrl({ spaceKey })}
						{...tooltipProps}
						onClick={onClick}
						testId={testId}
					>
						<FormattedMessage {...i18n.bulkToolsItemLabel} />
						<PremiumLozengeLoadable />
					</LinkItem>
				)}
			</Tooltip>
		</CustomItem>
	);
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CustomItem = styled.li({
	position: 'relative',
	borderRadius: '3px',
});

const isOnBulkToolsArgs = {
	selector: (routeName: string | undefined) => {
		if (!routeName) {
			return false;
		}
		return routeName === SPACE_BULK_TOOLS.name;
	},
};
